import React, { useState, useEffect } from 'react';
import Navigation from '../../Components/Navigation';
import { FiEye, FiTrash2, FiCheck, FiX } from 'react-icons/fi';
import './Enquiry_page.css';

const Enquiry_page = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const fetchEnquiries = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('https://tstock.trillionstechsolutions.com/enquiry-files/all');
        if (!response.ok) throw new Error('Failed to fetch enquiries');
        const result = await response.json();
        // Map API data to UI expected format
        const mapped = (result.data || []).map(item => ({
          id: item._id,
          name: item.fullName,
          email: item.emailAddress,
          subject: item.enquiryNumber, // Using enquiryNumber as subject
          message: item.message,
          date: item.createdAt,
          status: item.status ? item.status.toLowerCase() : 'pending',
          priority: 'medium', // Default priority, adjust as needed
          phone: item.phoneNumber
        }));
        setEnquiries(mapped);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchEnquiries();
  }, []);

  const handleViewEnquiry = (enquiry) => {
    setSelectedEnquiry(enquiry);
  };

  const handleDeleteEnquiry = (id) => {
    setEnquiries(enquiries.filter(enquiry => enquiry.id !== id));
    if (selectedEnquiry?.id === id) {
      setSelectedEnquiry(null);
    }
  };

  const handleUpdateStatus = (id, newStatus) => {
    setEnquiries(enquiries.map(enquiry => 
      enquiry.id === id ? { ...enquiry, status: newStatus } : enquiry
    ));
    if (selectedEnquiry?.id === id) {
      setSelectedEnquiry({ ...selectedEnquiry, status: newStatus });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return '#ff4444';
      case 'medium':
        return '#ffbb33';
      case 'low':
        return '#00C851';
      default:
        return '#888';
    }
  };

  const filteredEnquiries = enquiries.filter(enquiry => {
    const matchesSearch = 
      enquiry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enquiry.subject.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || enquiry.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="page-container">
      <Navigation />
      <div className="enquiry-container">
        <div className="enquiry-header">
          <h1>User Enquiries</h1>
          <div className="enquiry-filters">
            <input
              type="text"
              placeholder="Search enquiries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="status-filter"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>

        <div className="enquiry-content">
          {loading ? (
            <div className="loading">Loading enquiries...</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <div className="enquiry-list">
              {filteredEnquiries.map(enquiry => (
                <div key={enquiry.id} className="enquiry-card">
                  <div className="enquiry-card-header">
                    <div className="enquiry-info">
                      <h3>{enquiry.subject}</h3>
                      <span className="enquiry-date">{formatDate(enquiry.date)}</span>
                    </div>
                    <div className="enquiry-actions">
                      <button
                        className="view-btn"
                        onClick={() => handleViewEnquiry(enquiry)}
                      >
                        <FiEye />
                      </button>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteEnquiry(enquiry.id)}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </div>
                  <div className="enquiry-card-body">
                    <p className="enquiry-message">{enquiry.message}</p>
                    <div className="enquiry-meta">
                      <span className="enquiry-name">{enquiry.name}</span>
                      <span className="enquiry-email">{enquiry.email}</span>
                      <span 
                        className="priority-badge"
                        style={{ backgroundColor: getPriorityColor(enquiry.priority) }}
                      >
                        {enquiry.priority}
                      </span>
                      <span className={`status-badge ${enquiry.status}`}>
                        {enquiry.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedEnquiry && (
            <div className="enquiry-detail">
              <div className="detail-header">
                <h2>Enquiry Details</h2>
                <button
                  className="close-btn"
                  onClick={() => setSelectedEnquiry(null)}
                >
                  <FiX />
                </button>
              </div>
              <div className="detail-content">
                <div className="detail-group">
                  <label>Name</label>
                  <p>{selectedEnquiry.name}</p>
                </div>
                <div className="detail-group">
                  <label>Email</label>
                  <p>{selectedEnquiry.email}</p>
                </div>
                <div className="detail-group">
                  <label>Subject</label>
                  <p>{selectedEnquiry.subject}</p>
                </div>
                <div className="detail-group">
                  <label>Message</label>
                  <p className="detail-message">{selectedEnquiry.message}</p>
                </div>
                <div className="detail-group">
                  <label>Date</label>
                  <p>{formatDate(selectedEnquiry.date)}</p>
                </div>
                <div className="detail-group">
                  <label>Priority</label>
                  <span 
                    className="priority-badge"
                    style={{ backgroundColor: getPriorityColor(selectedEnquiry.priority) }}
                  >
                    {selectedEnquiry.priority}
                  </span>
                </div>
                <div className="detail-group">
                  <label>Status</label>
                  <div className="status-actions">
                    <button
                      className={`status-btn ${selectedEnquiry.status === 'resolved' ? 'active' : ''}`}
                      onClick={() => handleUpdateStatus(selectedEnquiry.id, 'resolved')}
                    >
                      <FiCheck /> Resolved
                    </button>
                    <button
                      className={`status-btn ${selectedEnquiry.status === 'pending' ? 'active' : ''}`}
                      onClick={() => handleUpdateStatus(selectedEnquiry.id, 'pending')}
                    >
                      <FiX /> Pending
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Enquiry_page;
